import { gtmSendEvent } from "@wojo/analytics";
import dayjs from "dayjs";
import {
    BusinessCode,
    IdentifyCustomerFragment,
    useIdentifyMeQuery,
} from "../generated/graphql";
import { useEffect } from "react";

type IdentifyData = {
    user_id: string;
    first_booking_date: string;
    is_future_booked: string;
    is_on_tour: string;
    nb_days_since_last_booking: string;
    nb_days_since_recent_tour: string;
    nb_days_until_next_departure: string;
    nb_future_trips: string;
    nb_past_trips: string;
    nb_wishlist_items: string;
    user_type: string;
};
// Exported for testing
export const toIdentifyData = (
    data: IdentifyCustomerFragment,
): IdentifyData => {
    // We want this to be ascending order
    const sortedByDepartureTrips = data.confirmedTrips.toSorted(
        (a, b) => a.departureDate.localeCompare(b.departureDate) * -1,
    );
    // We want this to be descending order
    const sortedByBooked = data.confirmedTrips.toSorted((trip1, trip2) =>
        trip1.dateBooked.localeCompare(trip2.dateBooked),
    );

    const { onTour, postTour, preTour } = sortedByDepartureTrips.reduce(
        (acc, trip) => {
            if (trip.timingStatus === "OnTour") {
                acc.onTour.push(trip);
            } else if (trip.timingStatus === "PostTour") {
                acc.postTour.push(trip);
            } else {
                acc.preTour.push(trip);
            }
            return acc;
        },
        {
            onTour: [] as IdentifyCustomerFragment["confirmedTrips"],
            postTour: [] as IdentifyCustomerFragment["confirmedTrips"],
            preTour: [] as IdentifyCustomerFragment["confirmedTrips"],
        },
    );

    const nbDaysSinceRecentTour =
        onTour.length > 0 ? "0" : daysFromNow(postTour.at(0)?.departureDate);

    return {
        user_id: data._id,
        first_booking_date: sortedByBooked.at(0)?.dateBooked ?? "",
        is_future_booked: preTour.length > 0 ? "true" : "false",
        is_on_tour: onTour.length > 0 ? "true" : "false",
        nb_days_since_last_booking: daysFromNow(
            sortedByBooked.at(-1)?.dateBooked,
        ),
        nb_days_since_recent_tour: nbDaysSinceRecentTour,
        nb_days_until_next_departure: daysFromNow(preTour.at(0)?.departureDate),
        nb_future_trips: preTour.length.toString(),
        nb_past_trips: postTour.length.toString(),
        nb_wishlist_items: data.wishlist.length.toString(),
        user_type: "customer",
    };
};

export const useIdentify = () => {
    const { data } = useIdentifyMeQuery({
        variables: {
            businessCode: process.env.NEXT_PUBLIC_BUSINESS_CODE as BusinessCode,
        },
    });

    useEffect(() => {
        const identifyData =
            data?.me?.__typename === "Customer"
                ? toIdentifyData(data.me)
                : null;
        const jsonIdentifyData = identifyData
            ? JSON.stringify(identifyData)
            : null;
        const lastRun = localStorage.getItem("identify");
        if (!!jsonIdentifyData && jsonIdentifyData !== lastRun) {
            gtmSendEvent({
                event: "identify",
                ...identifyData,
            });
            sessionStorage.setItem("identify", jsonIdentifyData);
        }
    }, [data]);
};

const daysFromNow = (departureDate?: string) => {
    if (!departureDate) {
        return "";
    }
    const now = dayjs.utc();
    const departure = dayjs.utc(departureDate);

    return Math.abs(departure.diff(now, "days")).toString();
};
